import { Group, Stack, Text } from '@mantine/core'
import { StableKey } from 'app/models/common-types'
import { RefinedSubTrade, TradedFacilityParams } from 'app/models/traded-facilities-params'
import MainDrawer from 'app/views/components/Drawer/MainDrawer'
import SegmentControlUpdated from 'app/views/components/segments/segment-control-updated'
import MainTable from 'app/views/components/Table/MainTable'
import { SetStateAction, useEffect, useState } from 'react'
import { columnDefsAccrualFeeCalculation } from './sub-trade-facility-accrual-fee-calculation-columnDefs'
import { useListState } from '@mantine/hooks'
import { calculateFeeByDays } from 'app/state/ducks/traded-facilities/thunks'
import { formatPriceWithoutCurrency } from 'app/utils/util-functions'
import ExportToCsv from 'app/views/components/ExportToCsv/ExportToCsv'
import { FMFeeSections } from 'app/models/fm-metadata-params'
import { feeSectionNotes } from './fee-section-notes'

interface Props {
  open: boolean
  setOpen: React.Dispatch<SetStateAction<boolean>>
  facilityId: StableKey
  subTrade: RefinedSubTrade
  /** Funding Memo fee-section states: a nil DC / CoC is stated above the per-day grid. */
  feeSections?: FMFeeSections | null
}

export default function SubTradeFacilityFeeDrawer({ open, setOpen, subTrade, facilityId, feeSections }: Props) {
  const [tab, setTab] = useState<string>(facilityId.id)
  const [loader, setLoader] = useState<boolean>(false)
  const [feesByDays, feesByDaysHandler] = useListState<any>([])
  const [selectedTF, setSelectedTF] = useState<TradedFacilityParams>();
  const [totalFee, setTotalFee] = useState<number>(0.0);
  const [calculatedData, setCalculatedData] = useState<any[]>([])

  const tabs = subTrade.tradedFacilities.reduce((tabs, tf) => {
    const name = tf.facilityDetails?.name ?? ''
    const identifier = tf.facilityDetails?.cusip || tf.facilityDetails?.isin
    const oneTab = {
      route: tf.facilityId.id,
      stopNavigation: true,
      label: identifier ? `${name} - (${identifier})` : name,
      active: tab === tf.facilityId.id,
    }
    tabs[tf.facilityId.id] = oneTab
    return tabs
  }, {} as { [key in string]: { route: string; label: string; active: boolean; stopNavigation: boolean }
  })

  const calculateFeesForFacility = async (facilityId: string) => {
    setLoader(true)
    const data: any[] = await calculateFeeByDays(facilityId, subTrade.id || '')
    setCalculatedData(data)
    setLoader(false)
  }

  useEffect(() => {
    const resultMap: Record<string, Record<string, string | number>> = {};
    resultMap['Total'] = { effectiveDate: 'Total' };

    calculatedData.forEach(({ feeType, amountWithDate }) => {
      amountWithDate.forEach(({ effectiveDate, amount }: any) => {
        if (!resultMap[effectiveDate]) {
          resultMap[effectiveDate] = { effectiveDate };
        }
        const fee = selectedTF?.fees.find((fee: any) => fee.feeType === feeType);
        const isNegative = fee && fee.paidTo === 'PaidToBuyer'

        resultMap[effectiveDate][feeType] = Number(amount) * (isNegative ? -1 : 1);
        resultMap.Total[feeType] = (Number(resultMap.Total[feeType]) || 0) + (isNegative ? -1 * Number(amount) : Number(amount));

        resultMap[effectiveDate].total = (Number(resultMap[effectiveDate].total) || 0) + (isNegative ? -1 * Number(amount) : Number(amount));
        resultMap.Total.total = (Number(resultMap.Total.total) || 0) + (isNegative ? -1 * Number(amount) : Number(amount));
      });
    });

    setTotalFee(Number(resultMap.Total.total))
    feesByDaysHandler.setState(Object.values(resultMap).sort((a, b) => a.effectiveDate > b.effectiveDate ? 1 : -1))

  }, [calculatedData, selectedTF])


  useEffect(() => {
    if (!open) return
    setSelectedTF(subTrade.tradedFacilities.find(tf => tf.facilityId.id === tab))
    calculateFeesForFacility(tab)
  }, [tab, open])

  const facilityName = selectedTF?.facilityDetails ? selectedTF.facilityDetails.name : ''
  const facilityCusip = selectedTF?.facilityDetails?.cusip ? `(${selectedTF.facilityDetails.cusip})` : ''

  const feesByDaysArrayObjects = () => {
    const toNumber = (v: any): number => {
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    };

    const formatValue = (n: number) => {
      const clean = Object.is(n, -0) ? 0 : n;
      return clean < 0
        ? `(${formatPriceWithoutCurrency(Math.abs(clean), 2, 2)})`
        : formatPriceWithoutCurrency(clean, 2, 2);
    };

    return feesByDays.map((item) => {
      const total = toNumber(item.total);
      const econBenefit = toNumber(item['Economic Benefit']);
      const delayedComp = toNumber(item['Delayed Compensation']);
      const costOfCarry = toNumber(item['Cost of Carry']);

      return {
        Date: item.effectiveDate,      // keep raw
        Total: formatValue(total),
        'Economic Benefit': formatValue(econBenefit),
        'Delayed Compensation': formatValue(delayedComp),
        'Cost of Carry': formatValue(costOfCarry),
      };
    });
  };

  const csvName = (facilityName + " " + facilityCusip + '.csv').toString()

  return (
    <MainDrawer
      opened={open}
      setOpen={setOpen}
      titleText="Calculations"
      fromSize="60%"
    >
      <Group w="100%" position="apart" className='tabsGapsActivity' mt='20px'>
        <Group position="right">
          <SegmentControlUpdated tabs={tabs} onTabChange={(selectedTab) => setTab(selectedTab)} />
        </Group>
      </Group>
      <Stack spacing="20px !important" justify="space-between" h="100%" >
        <div className='sub-trade-fee-drawer-header'>
          <Text className='sub-trade-fee-drawer-header-text'>
            Total: {Number(totalFee) ? Number(totalFee) < 0.0 ? `(${formatPriceWithoutCurrency(Math.abs(Number(totalFee)), 2, 2)})` : formatPriceWithoutCurrency(Number(totalFee), 2, 2) : 0.0}
          </Text>
        </div>
        {feeSectionNotes(feeSections).map((note) => (
          <Text key={note.feeType} className='accruing-fees-nil-note'>
            <span className='accruing-fees-nil-note-fee'>{note.feeType}:</span> {note.statement}
          </Text>
        ))}
        <MainTable
          tableName="Fee calcs"
          columnDefs={columnDefsAccrualFeeCalculation}
          data={feesByDays}
          setRow={() => { }}
          headerBackgroundColor="#F0EEEE"
          enableTopToolbar={false}
          withPagination={false}
          csvExportEnabled={false}
          enableColumnResizing={false}
          enableColumnOrdering={false}
          enableSorting={false}
          enableGrouping={false}
          minHeight="0px"
          isLoading={loader}
          border={true}
        />

      </Stack>

      <Group position="right" mt="20px">
        <ExportToCsv csvName={csvName} exportObj={feesByDaysArrayObjects()} style={{
          width: '130px',
          backgroundColor: '#A4CAFE',
          color: 'black',
          fontWeight: 'bold',
          borderRadius: '8px',
        }} />
      </Group>
    </MainDrawer >


  )
}
